<template>
    <RedesignBaseChart
        :option="option"
        :widget="data"
        :hide="showDataLoadingAnimation || showNoData"
        :loading="showDataLoadSpinner"
        :tooltip-pin="hasAllowedSeries"
    >
        <template #pre>
            <DataLoadAnimation
                v-if="showDataLoadingAnimation"
            />

            <RedesignWidgetNoData
                v-else-if="showNoData"
                :widget="data"
                class="h-full"
            />
        </template>
    </RedesignBaseChart>
</template>

<script lang="ts" setup>
import { provide, computed } from 'vue'
import { THEME_KEY } from 'vue-echarts'
import { WidgetExtended } from '@/types/dashboard'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import RedesignWidgetNoData from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetNoData.vue'
import DataLoadAnimation from '@/components/DataLoadAnimation.vue'
import { ChartOptions } from '@/types/echart'
import { formatEchartOptions } from '@/helpers/echart'
import { hasAllowedSeriesTypes } from '@/composables/useChartTooltipPin'

/* Types */
type ChartDataProcessor = (data: ChartOptions) => ChartOptions

/* Props */
const props = defineProps<{
    data: WidgetExtended
    dataProcessors?: Array<ChartDataProcessor>
}>()

/* Provide */
provide(THEME_KEY, 'light')

/* Composable */
const {
    data: chartOptions,
    isLoading,
    dataLoaded
} = useWidgetDataWithRefreshInterval<
    ChartOptions,
    { data: WidgetExtended },
    'data'
>(props, 'data')

/* Computed */
const rawChartOptions = computed((): ChartOptions | undefined => {
    const raw = chartOptions.value
    if (!raw) {
        return
    }
    return Array.isArray(raw) ? raw[0] : raw
})

const option = computed(() => {
    if (!rawChartOptions.value) {
        return
    }

    let finalProcessors = [
        formatEchartOptions
    ]

    if (props.dataProcessors) {
        finalProcessors = finalProcessors.concat(props.dataProcessors)
    }

    return finalProcessors.reduce<ChartOptions>(
        (acc, item) => {
            return item(acc, props.data)
        },
        rawChartOptions.value
    )
})
const noData = computed(() => {
    return !option.value
        || typeof option.value !== 'object'
        || Array.isArray(option.value)
        || Object.keys(option.value).length === 0

})
const showDataLoadingAnimation = computed(() => {
    return isLoading.value && !dataLoaded.value
})
const showNoData = computed(() => {
    return dataLoaded.value && noData.value
})
const showDataLoadSpinner = computed(() => {
    return isLoading.value && dataLoaded.value
})
const hasAllowedSeries = computed(() => hasAllowedSeriesTypes(option.value, ['heatmap']))
</script>
